import { DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { TargetBox } from 'components/DragDrop'
import { useRef, useState } from 'react'
import { NativeTypes } from 'react-dnd-html5-backend'
import { toast } from 'react-toastify'
import { type BudgetAiExtractResult, extractBudgetFromPdf, uploadFiles } from 'services'
import { Button, Modal } from 'stories/components'
import { getPrice2decimal } from 'utils'

type Props = {
  isOpen: boolean
  loanNumber: number
  budgetId: number
  onClose: () => void
  onApply: (result: BudgetAiExtractResult) => Promise<void> | void
  onAddUnmatched?: (unmatched: BudgetAiExtractResult['unmatched']) => void
}

export const BudgetAiExtractModal = ({
  isOpen,
  loanNumber,
  budgetId,
  onClose,
  onApply,
  onAddUnmatched,
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [loading, setLoading] = useState(false)
  const [fileName, setFileName] = useState('')
  const [result, setResult] = useState<BudgetAiExtractResult | null>(null)

  const reset = () => {
    setLoading(false)
    setFileName('')
    setResult(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const runExtractFromFileKey = async (fileKey: string, name: string) => {
    setLoading(true)
    setResult(null)
    setFileName(name)
    try {
      const res = await extractBudgetFromPdf(budgetId, [{ fileKey: String(fileKey), name }])
      if (res?.success && res.data) {
        const data: BudgetAiExtractResult = {
          ...res.data,
          unmatched: Array.isArray(res.data.unmatched) ? res.data.unmatched : [],
        }
        setResult(data)
        if (!data.items.length && !data.unmatched.length && !data.narrative) {
          toast('No budget items were found in the document.', { type: 'warning' })
        }
      } else {
        toast(res?.message || 'Failed to extract budget items from the document.', { type: 'error' })
      }
    } catch {
      toast('Failed to extract budget items from the document.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const runExtract = async (file: File) => {
    setLoading(true)
    setResult(null)
    setFileName(file.name)
    try {
      const keys: string[] = await uploadFiles(
        {
          path: `loan/${loanNumber}/budget-ai`,
        },
        [file],
      )
      const fileKey = keys?.[0]
      if (!fileKey) {
        toast('Failed to upload budget document.', { type: 'error' })
        return
      }

      const res = await extractBudgetFromPdf(budgetId, [{ fileKey: String(fileKey), name: file.name }])
      if (res?.success && res.data) {
        const data: BudgetAiExtractResult = {
          ...res.data,
          unmatched: Array.isArray(res.data.unmatched) ? res.data.unmatched : [],
        }
        setResult(data)
        if (!data.items.length && !data.unmatched.length && !data.narrative) {
          toast('No budget items were found in the document.', { type: 'warning' })
        }
      } else {
        toast(res?.message || 'Failed to extract budget items from the document.', { type: 'error' })
      }
    } catch {
      toast('Failed to extract budget items from the document.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onFilePicked = (files: FileList | File[] | null) => {
    const file = files?.[0]
    if (!file) return
    runExtract(file)
  }

  const onDropItem = (item: any) => {
    if (item?.files?.length) {
      onFilePicked(item.files)
      return
    }

    const docs: any[] = Array.isArray(item?.documents) ? item.documents : [item]
    const doc = docs.find((d) => d?.fileKey || d?.key) || docs[0]
    const fileKey = doc?.fileKey || doc?.key
    const name = doc?.name || 'document'
    if (!fileKey) {
      toast('Could not read the dropped document.', { type: 'error' })
      return
    }
    runExtractFromFileKey(String(fileKey), name)
  }

  const unmatched = result?.unmatched || []
  const canApplyMatched = !!(result?.items?.length || result?.narrative)
  // A budget in the lender's own format matches nothing on the roster, so every line arrives
  // unmatched. Leaving the only button disabled read as "the file did not work"; the next step is
  // the assign screen, so let the primary action take you there.
  const canContinue = canApplyMatched || unmatched.length > 0
  const continueToAssign = !canApplyMatched && unmatched.length > 0 && !!onAddUnmatched

  return (
    <Modal
      title="Extract Budget from Document"
      isOpen={isOpen}
      onClose={handleClose}
      titleOkay={continueToAssign ? `Assign ${unmatched.length} Items` : canContinue ? 'Apply Matched Items' : ''}
      onOk={async () => {
        if (!canContinue || !result) return
        if (canApplyMatched) await onApply(result)
        if (unmatched.length && onAddUnmatched) onAddUnmatched(unmatched)
        handleClose()
      }}
      loading={loading}
      disabled={!canContinue}
    >
      <div className="w-[720px] max-w-full space-y-4">
        <p className="text-sm text-gray-600">
          Drop a budget / scope-of-work document (PDF, Word, Excel, image, or other common formats — from your computer
          or the Documents floater). AI will match lines to existing Budget & Scope of Work rows. Review the matches,
          then apply.
        </p>

        <TargetBox
          types={['document', NativeTypes.FILE]}
          onDrop={onDropItem}
          className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors border-gray-300 hover:border-shade-blue"
          highlightClassName="border-shade-blue bg-blue-50"
        >
          <div onClick={() => inputRef.current?.click()}>
            <DocumentArrowUpIcon className="w-10 h-10 mx-auto text-shade-blue mb-2" />
            <div className="font-medium text-gray-800">Drop budget document here</div>
            <div className="text-sm text-gray-500 mt-1">or click to browse — all file formats accepted</div>
            {fileName && <div className="text-xs text-gray-500 mt-2">Selected: {fileName}</div>}
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={(e) => onFilePicked(e.target.files)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </TargetBox>

        {loading && <div className="text-sm text-shade-blue font-medium">AI is reading the budget document…</div>}

        {result && (
          <div className="space-y-4">
            {result.narrative && (
              <div>
                <div className="font-semibold mb-2">Narrative</div>
                <div className="text-sm text-gray-700 border rounded p-2 max-h-24 overflow-auto whitespace-pre-wrap">
                  {result.narrative}
                </div>
              </div>
            )}

            <div>
              <div className="font-semibold mb-2">Matched items ({result.items.length})</div>
              {result.items.length === 0 ? (
                <div className="text-sm text-gray-500">No lines matched existing budget rows.</div>
              ) : (
                <div className="overflow-auto border rounded max-h-64">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left">
                      <tr>
                        <th className="p-2">Title</th>
                        <th className="p-2 text-right">Estimated</th>
                        <th className="p-2 text-right">Prepaid</th>
                        <th className="p-2">Cost Type</th>
                        <th className="p-2">Work to Be Performed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.items.map((item, idx) => (
                        <tr key={`${item.sectionValue}-${item.lineKey}-${idx}`} className="border-t">
                          <td className="p-2">{item.title || '—'}</td>
                          <td className="p-2 text-right">
                            {item.rehabCostBorrowerEstimated != null
                              ? getPrice2decimal(item.rehabCostBorrowerEstimated, false, true)
                              : '—'}
                          </td>
                          <td className="p-2 text-right">
                            {item.rehabCostAlreadyPrepaid != null
                              ? getPrice2decimal(item.rehabCostAlreadyPrepaid, false, true)
                              : '—'}
                          </td>
                          <td className="p-2">{item.costType || '—'}</td>
                          <td className="p-2 text-gray-600 max-w-[200px] truncate" title={item.description || ''}>
                            {item.description || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {!!unmatched.length && (
              <div>
                <div className="font-semibold mb-2 text-amber-700">Unmatched ({unmatched.length})</div>
                <div className="overflow-auto border rounded max-h-40">
                  <table className="w-full text-sm">
                    <thead className="bg-amber-50 text-left">
                      <tr>
                        <th className="p-2">Title</th>
                        <th className="p-2 text-right">Estimated</th>
                        <th className="p-2 text-right">Prepaid</th>
                        <th className="p-2">Cost Type</th>
                        <th className="p-2">Work to Be Performed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmatched.map((item, idx) => (
                        <tr key={`${item.title}-${idx}`} className="border-t">
                          <td className="p-2">{item.title || '—'}</td>
                          <td className="p-2 text-right">
                            {item.rehabCostBorrowerEstimated != null
                              ? getPrice2decimal(item.rehabCostBorrowerEstimated, false, true)
                              : '—'}
                          </td>
                          <td className="p-2 text-right">
                            {item.rehabCostAlreadyPrepaid != null
                              ? getPrice2decimal(item.rehabCostAlreadyPrepaid, false, true)
                              : '—'}
                          </td>
                          <td className="p-2">{item.costType || '—'}</td>
                          <td className="p-2 text-gray-500 max-w-[200px] truncate" title={item.description || ''}>
                            {item.description || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  These are not on the budget yet. Continuing opens the assign step, where each one is mapped to an
                  existing line, or added as a new item under the document's own sections when the budget has none to
                  match.
                </p>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                link
                color="gray"
                onClick={() => {
                  reset()
                }}
              >
                Choose another file
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
